<template>
  <main class="bg-white min-h-screen flex flex-col">
    <div class="flex flex-grow max-md:flex-col">
      <aside
        class="flex flex-col w-1/5 max-md:ml-0 max-md:w-full bg-primary-purple"
      >
        <div class="flex flex-col mt-14 flex-grow">
          <div class="flex flex-col ml-10 text-left">
            <!-- First Image -->
            <span class="text-white text-3xl font-semibold leading-9">
              Get Started <br />
            </span>
          </div>

          <StepsList :currentStep="1" />
        </div>
      </aside>
      <section
        class="flex flex-col w-4/5 justify-center mt-10 pt-6 pb-1 max-md:w-full"
      >
        <div class="flex justify-center mb-4">
          <div
            v-if="errorMessage"
            role="alert"
            class="flex gap-2 w-1/2 justify-center p-2.5 mt-6 text-sm leading-5 text-center text-red-900 bg-red-50 rounded-lg max-md:flex-wrap"
            style="max-height: 80px; overflow-y: auto"
          >
            <!-- Icon Container -->
            <div
              class="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full"
            >
              <!-- Custom Image Icon -->
              <img
                src="../../assets/shared/failure.svg"
                alt="Error Icon"
                class="w-4 h-4"
              />
            </div>
            <p>{{ errorMessage }}</p>
          </div>
        </div>
        <div
          class="flex flex-col w-1/3 mx-auto justify-center max-md:mt-4 max-md:w-full"
        >
          <h1 class="text-3xl font-semibold leading-9 text-left text-zinc-800">
            Expert Program Details
          </h1>
          <p class="mt-3 text-base leading-6 text-left text-neutral-700">
            Create your account to get started
          </p>
          <form id="expertProgramForm">
            <div class="relative mx-auto">
              <!-- SoftwareSearch Section -->
              <div
                class="relative z-20 flex gap-5 justify-between mt-8 text-left"
              >
                <div class="flex flex-col flex-grow basis-1/2">
                  <BaseLabel
                    forAttr="Software"
                    text="Your Company"
                    class="text-sm font-medium leading-5 text-zinc-800"
                  />
                  <span class="text-xs"> Add your company </span>
                  <!-- SoftwareSearch has to stack above the rest -->
                  <SoftwareSearch @selectProgram="selectProgram" />
                </div>
              </div>

              <!-- Summary Section -->
              <div class="relative mt-3 text-left z-10">
                <label
                  for="about"
                  class="self-start mb-2 mt-4 text-sm font-medium leading-none text-gray-700"
                >
                  About The Program
                </label>
                <textarea
                  ref="aboutTextarea"
                  id="about"
                  v-model="about"
                  :rows="rows"
                  maxlength="1057"
                  @input="handleAboutInput"
                  required
                  placeholder=""
                  class="resize-none w-full p-2 text-base text-gray-500 bg-white border rounded-md shadow-sm focus:outline-none"
                  :class="{
                    'border-red-500': aboutError,
                    'border-gray-300': !aboutError,
                  }"
                ></textarea>
                <span
                  class="mt-2 text-sm leading-none ml-3 px-1 text-left max-md:max-w-full"
                  :class="{
                    'text-red-500': aboutError,
                    'text-gray-500': !aboutError,
                  }"
                  >{{ about.length }}/1056</span
                >
              </div>

              <div class="flex flex-col mt-4">
                <BaseLabel
                  forAttr="settings"
                  text="Settings"
                  class="text-lg font-medium leading-5 text-zinc-800 text-left"
                />
              </div>

              <!-- Payout and Fee Section -->
              <div class="relative mt-4 text-left z-10">
                <div class="flex flex-col 2xl:flex-row gap-2">
                  <!-- First Input Field with Label -->
                  <div class="w-full 2xl:w-3/5">
                    <BaseLabel
                      forAttr="payout"
                      text="Expert Thank You Payout per Meeting"
                      class="text-sm font-medium leading-5 text-zinc-800"
                    />
                    <div>
                      <CurrencyInput
                        v-model="payout"
                        leadingText="$"
                        trailingText="USD"
                        placeholder="100"
                      />
                    </div>
                  </div>

                  <!-- Second Input Field with Label -->
                  <div class="w-full 2xl:w-2/5">
                    <BaseLabel
                      forAttr="fee"
                      :text="`SlashExperts Fee (${customPayoutFeePercentage()}%)`"
                      class="text-sm font-medium leading-5 text-zinc-800"
                    />
                    <span
                      id="fee"
                      class="px-3.5 py-2.5 text-sm leading-5 text-gray-500 whitespace-nowrap bg-gray-100 rounded-md shadow-sm w-full block"
                    >
                      $ {{ (payout * customPayoutFeePercentage()) / 100 }}
                    </span>
                  </div>
                </div>
                <span class="text-xs text-gray-500"
                  >Total Cost Per Meeting: ${{
                    payout + payout * (customPayoutFeePercentage() / 100)
                  }}</span
                >
              </div>

              <!-- company linkedin url -->
              <div class="relative flex flex-col mt-6 text-zinc-800">
                <BaseLabel
                  forAttr="linkedinURL"
                  text="Your Company Linkedin URL"
                  class="text-sm font-medium leading-5 text-zinc-800 text-left"
                />
                <div class="relative">
                  <input
                    id="linkedinUrl"
                    type="text"
                    v-model="linkedinUrl"
                    placeholder="https://www.linkedin.com/company/companyname"
                    class="px-3.5 py-2.5 mt-1 w-full bg-white rounded-md shadow-sm pr-1 border border-gray-300 border-solid"
                    required
                  />
                </div>
              </div>

              <!-- Goals Section -->
              <div
                class="relative flex flex-col justify-start mt-6 text-base font-medium leading-6 z-10"
              >
                <BaseLabel
                  forAttr="Goals"
                  text="Goals & Use Cases"
                  class="text-lg font-medium leading-5 text-zinc-800 text-left"
                />
                <GoalsCheckboxList
                  v-model:convertWebsiteVisitors="convertWebsiteVisitors"
                  v-model:generateIntentSignals="generateIntentSignals"
                  v-model:enableSales="enableSales"
                  v-model:engageNewCustomer="engageNewCustomer"
                  v-model:driveConversion="driveConversion"
                  v-model:sdrSequenceStep="sdrSequenceStep"
                  v-model:lateStageCustomerReferences="
                    lateStageCustomerReferences
                  "
                />
              </div>
            </div>
          </form>
        </div>
        <FooterSection :isDisabled="isDisabled" @next="goNext" />
      </section>
    </div>
  </main>
</template>

<script lang="ts">
import {
  defineComponent,
  getCurrentInstance,
  onMounted,
  onUnmounted,
  ref,
} from "vue";
import FooterSection from "@/components/expert-program/FooterSection.vue";
import BaseLabel from "@/components/shared/BaseLabel.vue";
import GoalsCheckboxList from "@/components/expert-program/program-details/GoalsCheckboxList.vue";
import CurrencyInput from "@/components/shared/CurrencyInput.vue";
import SoftwareSearch from "@/components/expert-program/program-details/SoftwareSearch.vue";
import { Card } from "@/types/expert-program-types";
import { ApolloError, gql } from "@apollo/client/core";
import { useMutation } from "@vue/apollo-composable";
import StepsList from "@/components/expert-program/StepsList.vue";
import { CreateExpertProgram } from "@/types/user-store-types";
import router from "@/router";
import { useUserStore } from "@/stores/user";
import { PostHog } from "posthog-js";

const UPDATE_PROGRAM = gql`
  mutation updateProgram($input: UpdateProgramInput!) {
    updateProgram(input: $input) {
      program {
        id
      }
      errors
    }
  }
`;

export default defineComponent({
  name: "AddProgramDetails",
  components: {
    GoalsCheckboxList,
    BaseLabel,
    CurrencyInput,
    SoftwareSearch,
    FooterSection,
    StepsList,
  },
  setup() {
    const software = ref("");
    const about = ref("");
    const payout = ref(100);
    const rows = ref(8);
    const companyName = ref("");
    const email = ref("");
    const title = ref("");
    const linkedinUrl = ref("");
    const convertWebsiteVisitors = ref(false);
    const generateIntentSignals = ref(false);
    const enableSales = ref(false);
    const driveConversion = ref(false);
    const lateStageCustomerReferences = ref(false);
    const sdrSequenceStep = ref(false);
    const engageNewCustomer = ref(false);
    const companyWebsite = ref("");
    const password = ref("");
    const confirmPassword = ref("");
    const agreedToTerms = ref(false);
    const showPassword = ref(false);
    const showConfirmPassword = ref(false);
    const errorMessage = ref<string | null>(null);
    const tempDisable = ref(true);
    const isDisabled = ref(false);
    const selectedProgram = ref<Card | null>(null);
    const aboutError = ref(false);
    const userStore = useUserStore();
    const posthog = (getCurrentInstance()?.proxy as { $posthog: PostHog })
      ?.$posthog;

    const {
      mutate: updateProgram,
      onDone: updateProgramDone,
      onError: updateProgramError,
    } = useMutation(UPDATE_PROGRAM);

    const goNext = () => {
      errorMessage.value = "";
      const valid = validateInput();
      if (valid) {
        isDisabled.value = true;
        callUpdateProgram();
      }
    };

    const validateInput = () => {
      if (!selectedProgram.value) {
        errorMessage.value = "Please select a software";
        scrollToTop();
        return false;
      }

      if (!linkedinUrlValid()) {
        errorMessage.value =
          "Please enter a valid LinkedIn URL, such as https://www.linkedin.com/company/companyname";
        scrollToTop();
        return false;
      }

      return true;
    };

    const callUpdateProgram = () => {
      updateProgram({
        input: {
          programParams: {
            id: selectedProgram?.value?.id,
            name: selectedProgram?.value?.name,
            website: selectedProgram?.value?.website,
            description: about.value,
            payout: payout.value,
            ourFees: customPayoutFeePercentage(),
            linkedinCompanyUrl: linkedinUrl.value,
            convertWebsiteVisitors: convertWebsiteVisitors.value,
            generateIntentSignals: generateIntentSignals.value,
            enableSales: enableSales.value,
            driveConversion: driveConversion.value,
            lateStageCustomerReferences: lateStageCustomerReferences.value,
            sdrSequenceStep: sdrSequenceStep.value,
            engageNewCustomer: engageNewCustomer.value,
            onboardingStep: "billing",
          },
          claimProgram: true,
        },
      });
      updateProgramDone((response) => {
        if (response) {
          isDisabled.value = false;
          if (response?.data?.updateProgram?.errors?.length > 0) {
            errorMessage.value = response.data.updateProgram.errors.join(".");
            scrollToTop();
          } else {
            if (response?.data?.updateProgram?.program?.id) {
              storeProgramDetails();
            } else {
              scrollToTop();
              errorMessage.value = "Failed to store program details.";
            }
          }
        }
      });
      updateProgramError((error: ApolloError) => {
        isDisabled.value = false;
        errorMessage.value = error.message;
        scrollToTop();
        console.error("Submission failed:", error.message);
      });
    };

    const scrollToTop = () => {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    };

    const linkedinUrlValid = (): boolean => {
      const extractedUrl = extractLinkedInCompanyUrl(linkedinUrl.value);
      if (extractedUrl) {
        linkedinUrl.value = extractedUrl;
        return true;
      }
      return false;
    };

    const extractLinkedInCompanyUrl = (input: string): string | null => {
      // 1) Force a protocol so URL parser will work
      try {
        let url: URL;
        try {
          url = new URL(input.startsWith("http") ? input : `https://${input}`);
        } catch {
          return null; // not even a valid URL
        }

        // 2) Host must be linkedin.com (with optional one subdomain like "ca.")
        if (!/^(?:[\w-]+\.)?linkedin\.com$/i.test(url.hostname)) {
          return null;
        }

        // 3) Slug must only contain alphanumerice and hyphens
        if (
          !/^[a-zA-Z0-9-]+$/.test(
            url.href?.split("linkedin.com/company/")[1]?.split("/")[0]
          )
        ) {
          return null;
        }

        // 4) Pathname must be atleast /company/{slug} or /company/{slug}/
        const parts = url.pathname.split("/").filter(Boolean);
        if (parts.length < 2 || parts[0].toLowerCase() !== "company") {
          return null;
        }

        const slug = parts[1];
        // 5) Slug must be 1–100 chars of alphanumerice characters or hyphens only
        if (!/^[A-Za-z0-9_-]{1,100}$/.test(slug)) {
          return null;
        }

        return `https://www.linkedin.com/company/${slug}/`;
      } catch {
        return null;
      }
    };

    const selectProgram = (program: Card) => {
      selectedProgram.value = program;
      about.value = `Welcome to ${program.name}'s Expert Program! As an experienced user of our platform, you have the unique opportunity to share your success story with others who are exploring our solution. By joining as an Expert, you'll connect with prospective buyers in brief, meaningful conversations to share your implementation experience and insights. Your authentic feedback helps others make confident decisions while establishing you as a thought leader in your field. We're excited to have you join our community of trusted product champions!`;
    };

    const notifyPosthog = () => {
      if (posthog) {
        posthog.capture("Company_User_Completes_Program_Details", {
          email: userStore.email,
          software_name: selectedProgram.value?.name,
          about_program: about.value,
          payout: payout.value,
          linkedin_url: linkedinUrl.value,
          convert_website_visitors: convertWebsiteVisitors.value,
          generate_intent_signals: generateIntentSignals.value,
          enable_sales: enableSales.value,
          drive_conversion: driveConversion.value,
          late_stage_customer_references: lateStageCustomerReferences.value,
          sdr_sequence_step: sdrSequenceStep.value,
          engage_new_customer: engageNewCustomer.value,
        });
      }
    };

    const storeProgramDetails = () => {
      const expertProgram: CreateExpertProgram = {
        id: selectedProgram.value?.id ?? "",
        uuid: selectedProgram.value?.uuid ?? "",
        name: selectedProgram.value?.name ?? "",
        website: selectedProgram.value?.website ?? "",
        companyId: "",
        directoryCreated: false,
        widgetCreated: false,
        crmSynced: false,
        directoryButtonStyles: {
          backgroundColor: "",
          textColor: "",
          boldness: "",
          fontFamily: "",
          fontSize: "",
        },
        createdWidgetIds: [],
      };
      userStore.setCreateExpertProgram(expertProgram);
      notifyPosthog();
      if (userStore.createExpertProgram) {
        router.push({ name: "AddAccountBalance" });
      } else {
        errorMessage.value = "Failed to store program details.";
      }
    };

    const calculateRows = () => {
      const screenWidth = window.innerWidth;

      // Adjust rows based on screen width
      if (screenWidth < 600) {
        rows.value = 22; // For small screens (mobile)
      } else if (screenWidth < 900) {
        rows.value = 18; // For medium screens (tablet)
      } else if (screenWidth < 1260) {
        rows.value = 14; // For medium screens (tablet)
      } else {
        rows.value = 10; // For large screens (desktop)
      }

      console.log("Rows updated to:", rows.value, "Screen width:", screenWidth);
    };

    const handleAboutInput = () => {
      if (about.value.length > 1056) {
        aboutError.value = true;
        about.value = about.value.slice(0, 1056);
        setTimeout(() => {
          aboutError.value = false;
        }, 2000);
      }
    };

    const customPayoutFeePercentage = () => {
      if (userStore.customPayoutFee) {
        return userStore.customPayoutFee;
      }
      return 35;
    };

    onMounted(() => {
      scrollToTop();
      calculateRows();

      window.addEventListener("resize", calculateRows);
    });

    onUnmounted(() => {
      window.removeEventListener("resize", calculateRows);
    });

    return {
      software,
      payout,
      about,
      linkedinUrl,
      companyName,
      email,
      companyWebsite,
      convertWebsiteVisitors,
      generateIntentSignals,
      enableSales,
      driveConversion,
      isDisabled,
      goNext,
      lateStageCustomerReferences,
      sdrSequenceStep,
      engageNewCustomer,
      selectProgram,
      title,
      password,
      confirmPassword,
      agreedToTerms,
      showPassword,
      showConfirmPassword,
      errorMessage,
      tempDisable,
      rows,
      handleAboutInput,
      aboutError,
      customPayoutFeePercentage,
    };
  },
});
</script>
